import { StatusCode } from "api/protocols";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dequeue, enqueue, IUIActions, showError } from "redux/actions/ui";
import { ILocation, IModule, ISegment } from "redux/reducers/models/reservations";
import { Select } from "components/select/index";
import "./reservation.scss";
import Spin from "components/spin/spin";
import { ICustomer } from "redux/reducers/models/customer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faCircleInfo, faClock, faLocationDot, faMagnifyingGlass, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import CardSection from "components/cardSection/CardSection";
import Checkbox from "components/form/checkbox/Checkbox";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import { customerErrorMessage, formatDate, valueToString } from "helpers/Helpers";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { IAuthState } from "redux/reducers/auth";
import TextField from "components/form/textField/TextField";
import classNames from "classnames";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import { useAppDispatch } from "hooks/redux";
import Drawer from "components/drawerSlide/drawer";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import {
  GetReservationSegment,
  PostReservationBooking,
  PostReservationBookingNote,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import {
  GetCustomer,
  GetCustomerPaymentMethod,
  PostCustomer,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import {
  createEmptyGuestSearchState,
  getEffectiveReservationMaxBookingSize,
  resizeGuestSearchState,
} from "../settings/reservations/reservationGuestCountSelector/reservationBookingSize";
import { useReservationGuestSearch } from "../settings/reservations/reservationGuestCountSelector/useReservationGuestSearch";

enum BookingStep {
  Step1 = 1,
  Step2 = 2,
}

interface IProps {
  uiActions: IUIActions;
  authStore: IAuthState;
  date: Date;
  module: IModule;
  selectedSegment: ISegment;
  segments: Array<ISegment>;
  selectedLocation: ILocation;
  locations: Array<ILocation>;
  open: boolean;
  onSave: () => void;
  onCancel: () => void;
}

interface IBookingState {
  guestsAmount: number;
  durationKey: string;
  locationDurations: ILocationDurations;
  selectedLocationIds: Array<number>;
  selectedTab: number;
  searching: boolean;
  bookingParticipants: Array<ICustomer | null>;
  guestQueries: string[];
  guestSearchResults: Array<ICustomer[]>;
  selectedCardId: number;
  customerCreditCards: Array<Record<string, any>>;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  saveCard: boolean;
  addCard: boolean;
  note: string;

  bookingStep: BookingStep;
}

interface INewCustomerState {
  newCustomerSheetActive: boolean;
  currentSearch: string;
}

/**
 * @property {string}
 * @description key is the name of the duration and the value is an array of segment ids.
 * @example
 * {
 *
 * '0.5 Hour': [10], '1 Hour': [10,11], '1.5 Hour': [10,11,12]
 *
 * }
 *  */
interface IDuration {
  [key: string]: Array<number>;
}

/**
 * @property {string}
 * @description key is `location_id` and the value is `IDuration Object`.
 * @example
 *
 * {
 *
 * 1: { '0.5 Hour': [10], '1 Hour': [10,11], '1.5 Hour': [10,11,12] }
 *
 * 2: { '0.5 Hour': [25], '1 Hour': [25,26], '1.5 Hour': [25,26,27] }
 *
 * }
 *  */
type ILocationDurations = Record<string, IDuration>;

const durationMap = [
  { duration: 60, segmentMultFactor: 1 },
  { duration: 30, segmentMultFactor: 2 },
];

function minutesBetween(startTime: string, endTime: string) {
  const start = new Date(`1970-01-01T${startTime}`);
  const end = new Date(`1970-01-01T${endTime}`);
  return Math.floor(Math.abs(start.getTime() - end.getTime()) / 60000);
}

function formatDurationLabel(durationKey: string) {
  const hours = parseFloat(durationKey);
  if (!Number.isFinite(hours)) {
    return durationKey;
  }
  if (hours === 1) {
    return "1 hour";
  }
  return Number.isInteger(hours) ? `${hours} hours` : `${hours} hour`;
}

function buildLocationDurations(
  sourceSegments: ISegment[],
  startTime: string,
  selectedLocation: ILocation,
  locations: ILocation[],
): ILocationDurations {
  const locationDurations: ILocationDurations = {};
  if (!selectedLocation || !startTime) {
    return locationDurations;
  }

  const selectedFromStart = sourceSegments
    .filter(segment => segment.location_id === selectedLocation.id && segment.start_time >= startTime)
    .sort((left, right) => left.start_time.localeCompare(right.start_time));

  if (!selectedFromStart.length || selectedFromStart[0].start_time !== startTime) {
    return locationDurations;
  }

  const differenceInMinutes =
    selectedFromStart.length > 1
      ? minutesBetween(selectedFromStart[0].start_time, selectedFromStart[1].start_time)
      : 60;
  const lengthFactor = durationMap.find(duration => duration.duration === differenceInMinutes) ?? {
    duration: differenceInMinutes,
    segmentMultFactor: 1,
  };

  locations?.forEach(location => {
    const filteredSegments = sourceSegments
      .filter(segment => segment.location_id === location.id && segment.start_time >= startTime)
      .sort((left, right) => left.start_time.localeCompare(right.start_time));
    locationDurations[location.id] = {};
    if (!filteredSegments.length || filteredSegments[0].start_time !== startTime) {
      return;
    }

    const max = Math.min(5 * lengthFactor.segmentMultFactor, filteredSegments.length);
    const eventId = eventIdentity(filteredSegments[0]);
    let minutes = 0;
    for (let index = 0; index < max; index++) {
      minutes += differenceInMinutes;
      const candidate = filteredSegments[index];
      if (
        eventIdentity(candidate) === eventId &&
        candidate?.blocked_type === "open" &&
        candidate?.booking_id === null
      ) {
        const hours = minutes / 60;
        locationDurations[location.id][`${hours} Hour${hours > 1 ? "s" : ""}`] = filteredSegments
          .filter((_segment, segmentIndex) => segmentIndex <= index)
          .map(segment => segment.id);
      } else {
        break;
      }
    }
  });

  return locationDurations;
}

function eventIdentity(segment?: ISegment) {
  if (segment?.league_id) {
    return `league:${segment.league_id}`;
  }
  if (segment?.tournament_id) {
    return `tournament:${segment.tournament_id}`;
  }
  return "";
}

function isOpenForBooking(segment: ISegment, eventId: string) {
  return segment.blocked_type === "open" && !segment.booking_id && eventIdentity(segment) === eventId;
}

function pickStartTime(sourceSegments: ISegment[], locationId: number, preferred?: string) {
  const locationSegments = sourceSegments
    .filter(segment => segment.location_id === locationId)
    .sort((left, right) => left.start_time.localeCompare(right.start_time));
  const preferredSegment = preferred
    ? locationSegments.find(segment => segment.start_time === preferred)
    : undefined;
  const eventId = eventIdentity(preferredSegment);
  const starts = locationSegments.filter(segment => isOpenForBooking(segment, eventId));

  if (preferred && starts.some(segment => segment.start_time === preferred)) {
    return preferred;
  }
  return starts[0]?.start_time ?? "";
}

export default function ReservationBookingModalNew(props: IProps) {
  const {
    uiActions,
    date,
    module,
    onSave,
    onCancel,
    open,
    selectedSegment,
    segments,
    selectedLocation,
    locations,
    authStore,
  } = props;
  const maxBookingSize = useMemo(
    () => getEffectiveReservationMaxBookingSize(module?.max_booking_size),
    [module?.max_booking_size],
  );
  const [bookingState, setBookingState] = useState<IBookingState>(() => ({
    guestsAmount: 1,
    durationKey: "",
    locationDurations: null,
    selectedLocationIds: [],
    selectedTab: 0,
    searching: false,
    bookingParticipants: [],
    ...createEmptyGuestSearchState<ICustomer>(getEffectiveReservationMaxBookingSize(module?.max_booking_size)),
    selectedCardId: undefined,
    customerCreditCards: [],
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    saveCard: false,
    addCard: false,
    note: "",
    bookingStep: BookingStep.Step1,
  }));

  const [newCustomerState, setNewCustomerState] = useState<INewCustomerState>({
    newCustomerSheetActive: false,
    currentSearch: "",
  });
  const [scheduleSegments, setScheduleSegments] = useState<ISegment[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [bookingDate, setBookingDate] = useState<Date>(date);
  const [startTime, setStartTime] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const newCustomerSlotRef = useRef(0);

  const { Option } = Select;
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open || !selectedLocation || !locations?.length || !module) {
      setScheduleLoading(false);
      return;
    }

    let cancelled = false;
    let hasSeed = false;
    const preferredStart = selectedSegment?.start_time;
    const initialDate = date;
    setScheduleLoading(true);

    if (segments?.length && preferredStart) {
      const seededDurations = buildLocationDurations(segments, preferredStart, selectedLocation, locations);
      const keys = Object.keys(seededDurations[selectedLocation.id] ?? {});
      if (keys.length) {
        hasSeed = true;
        setScheduleSegments(segments);
        setStartTime(preferredStart);
        setBookingDate(initialDate);
        setBookingState(prevState => ({
          ...prevState,
          locationDurations: seededDurations,
          durationKey: keys[0],
          selectedLocationIds: [selectedLocation.id],
        }));
        setScheduleLoading(false);
      }
    }

    void (async () => {
      const dayPromise = GetReservationSegment({ module_id: module.id, date: formatDate(initialDate) }, false);
      if (!hasSeed && preferredStart) {
        const seedRes = await GetReservationSegment(
          { module_id: module.id, date: formatDate(initialDate), start_time: preferredStart },
          false,
        );
        if (!cancelled && seedRes.status === StatusCode.OK && seedRes.data?.length) {
          const seededDurations = buildLocationDurations(seedRes.data, preferredStart, selectedLocation, locations);
          const keys = Object.keys(seededDurations[selectedLocation.id] ?? {});
          if (keys.length) {
            hasSeed = true;
            setScheduleSegments(seedRes.data);
            setStartTime(preferredStart);
            setBookingDate(initialDate);
            setBookingState(prevState => ({
              ...prevState,
              locationDurations: seededDurations,
              durationKey: keys[0],
              selectedLocationIds: [selectedLocation.id],
            }));
            setScheduleLoading(false);
          }
        }
      }

      const segmentRes = await dayPromise;
      if (cancelled) {
        return;
      }
      if (segmentRes.status !== StatusCode.OK) {
        setScheduleLoading(false);
        uiActions.showError(t("secure.facility.reservation.reservation.005"));
        if (!hasSeed) {
          closeBookingModal();
        }
        return;
      }
      applySchedule(segmentRes.data ?? [], initialDate, preferredStart, !hasSeed);
      if (!cancelled) {
        setScheduleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedLocation?.id, module?.id]);

  useEffect(() => {
    setBookingState(prevState => {
      if (prevState.guestQueries.length === maxBookingSize) {
        return prevState;
      }

      const resizedGuestSearch = resizeGuestSearchState(
        prevState.guestQueries,
        prevState.guestSearchResults,
        maxBookingSize,
      );

      return {
        ...prevState,
        ...resizedGuestSearch,
      };
    });
  }, [maxBookingSize]);

  function applySchedule(
    daySegments: ISegment[],
    nextDate: Date,
    preferredStart: string | undefined,
    closeOnEmpty: boolean,
  ) {
    if (!selectedLocation || !locations?.length) {
      return;
    }

    const nextStart = pickStartTime(daySegments, selectedLocation.id, preferredStart);
    const locationDurations = buildLocationDurations(daySegments, nextStart, selectedLocation, locations);
    const keys = Object.keys(locationDurations[selectedLocation.id] ?? {});
    if (!nextStart || !keys.length) {
      uiActions.showError(t("secure.facility.reservation.reservation.008"));
      if (closeOnEmpty) {
        closeBookingModal();
      }
      return;
    }

    setScheduleSegments(daySegments);
    setStartTime(nextStart);
    setBookingDate(nextDate);
    setBookingState(prevState => {
      const durationKey = keys.includes(prevState.durationKey) ? prevState.durationKey : keys[0];
      const keptLocationIds = prevState.selectedLocationIds.filter(
        locationId => locationDurations[locationId]?.[durationKey]?.length > 0,
      );
      const selectedLocationIds =
        keptLocationIds.length || prevState.locationDurations
          ? keptLocationIds
          : locationDurations[selectedLocation.id]?.[durationKey]?.length
            ? [selectedLocation.id]
            : [];
      return { ...prevState, locationDurations, durationKey, selectedLocationIds };
    });
  }

  async function loadSchedule(nextDate: Date, preferredStart?: string) {
    if (!module) {
      return;
    }
    const segmentRes = await GetReservationSegment(
      { module_id: module.id, date: formatDate(nextDate) },
      false,
    );
    if (segmentRes.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation.005"));
      return;
    }
    applySchedule(segmentRes.data ?? [], nextDate, preferredStart, false);
  }

  async function searchCustomers(params: string) {
    const customerRes = await GetCustomer({ search: params }, false);

    if (customerRes.status !== StatusCode.OK) {
      console.log(customerRes?.message);
      return;
    }

    return customerRes?.data;
  }

  const handleGuestSearchResults = useCallback((slotIndex: number, results: ICustomer[]) => {
    setBookingState(prevState => {
      const guestSearchResults = [...prevState.guestSearchResults];
      guestSearchResults[slotIndex] = results;
      return { ...prevState, guestSearchResults };
    });
  }, []);

  const handleGuestSearching = useCallback((searching: boolean) => {
    setBookingState(prevState => ({ ...prevState, searching }));
  }, []);

  useReservationGuestSearch({
    guestQueries: bookingState.guestQueries,
    maxSlots: maxBookingSize,
    enabled: open,
    searchCustomers,
    onSlotResults: handleGuestSearchResults,
    onSearching: handleGuestSearching,
  });

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { id, value } = e.target;
    setBookingState(prevState => ({ ...prevState, [id]: value }));
  }

  async function loadCustomerPaymentMethods(customerId?: number) {
    const id = customerId ?? bookingState?.bookingParticipants[0]?.id;
    if (!id) {
      return;
    }
    const paymentMethodRes = await GetCustomerPaymentMethod(
      { customer_id: id },
      false,
    );
    if (paymentMethodRes?.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_new.003"));
      return;
    }
    setBookingState(prevState => ({ ...prevState, customerCreditCards: paymentMethodRes?.data }));
  }

  function changeGuestCount(amount: number) {
    const nextAmount = Math.min(maxBookingSize, Math.max(1, amount));
    setBookingState(prevState => ({
      ...prevState,
      guestsAmount: nextAmount,
      bookingParticipants: prevState.bookingParticipants.map((participant, index) =>
        index < nextAmount ? participant : null,
      ),
    }));
  }

  function handleDropDownChange(value: string | number, id: string) {
    if (!bookingState) {
      return;
    }

    const locationDurations = { ...bookingState.locationDurations };
    let selectedLocationIds = [...bookingState.selectedLocationIds];
    if (locationDurations && selectedLocationIds) {
      Object.keys(locationDurations)?.forEach(locationId => {
        if (!locationDurations[locationId][value] || locationDurations[locationId][value]?.length < 1) {
          selectedLocationIds = selectedLocationIds?.filter(location => location !== Number(locationId));
        }
      });
    }
    setBookingState(prevState => ({ ...prevState, [id]: value, selectedLocationIds }));
  }

  const handleCustomerSearch = (query: string, slotIndex: number) => {
    setBookingState(prevState => {
      const guestQueries = [...prevState.guestQueries];
      guestQueries[slotIndex] = query;
      return { ...prevState, guestQueries };
    });
    setNewCustomerState(prevState => ({ ...prevState, currentSearch: query }));
  };

  function assignGuest(slotIndex: number, customer: ICustomer | null) {
    setBookingState(prevState => {
      const bookingParticipants = [...prevState.bookingParticipants];
      bookingParticipants[slotIndex] = customer;
      const guestQueries = [...prevState.guestQueries];
      guestQueries[slotIndex] = "";
      const guestSearchResults = [...prevState.guestSearchResults];
      guestSearchResults[slotIndex] = [];
      return {
        ...prevState,
        bookingParticipants,
        guestQueries,
        guestSearchResults,
        ...(slotIndex === 0 && !customer
          ? { customerCreditCards: [], selectedCardId: undefined, addCard: false }
          : {}),
      };
    });
    if (slotIndex === 0 && customer?.id) {
      void loadCustomerPaymentMethods(customer.id);
    }
  }

  function addBay(locationId: number) {
    if (bookingState.selectedLocationIds.includes(locationId)) {
      return;
    }
    setBookingState(prevState => ({
      ...prevState,
      selectedLocationIds: [...prevState.selectedLocationIds, locationId],
    }));
  }

  function removeBay(locationId: number) {
    setBookingState(prevState => ({
      ...prevState,
      selectedLocationIds: prevState.selectedLocationIds.filter(id => id !== locationId),
    }));
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    if (bookingState.selectedCardId !== undefined) {
      setBookingState(prevState => ({ ...prevState, selectedCardId: undefined }));
    }

    setBookingState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  function onSelectCreditCard(id: number) {
    if (id > 0) {
      if (id === bookingState.selectedCardId) {
        // Deselect card
        setBookingState(prevState => ({ ...prevState, selectedCardId: undefined }));
      } else {
        setBookingState(prevState => ({ ...prevState, selectedCardId: id, addCard: false }));
      }
    } else {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_new.004"));
    }
  }

  async function collectCard(customer_id: number) {
    if (!stripe || !elements) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_new.005"));
      return;
    }

    // Loader for length of operation to stop user from clicking
    uiActions.enqueue();
    try {
      let setupCardRes;
      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (paymentMethod.paymentMethod) {
        setupCardRes = await setupCard(paymentMethod.paymentMethod.id, customer_id);
        uiActions.dequeue();
        return {
          customer_payment_method_id: setupCardRes,
        };
      } else {
        uiActions.showError(paymentMethod?.error?.message);
      }
    } catch {
      uiActions.dequeue();
    }

    uiActions.dequeue();
  }

  async function setupCard(payment_method: string, customer_id: number) {
    const setupRes = await PostSetup({ customer_id }, true);

    if (setupRes.status !== StatusCode.OK) {
      uiActions.showError(String(setupRes?.data?.message));
      return null;
    } else {
      if (payment_method !== "undefined") {
        const clientSecret =
          setupRes?.data?.setup_intent?.client_secret ?? setupRes?.data?.data?.setup_intent?.client_secret;
        if (!clientSecret) {
          uiActions.showError(t("Could not start card setup"));
          return null;
        }
        const stripeRes = await stripe.confirmCardSetup(String(clientSecret), {
          payment_method: payment_method,
        });

        if (stripeRes.error) {
          uiActions.showError(stripeRes?.error?.message);
        } else {
          const methodRes = await PostPaymentMethod({
            payment_method_id: stripeRes?.setupIntent?.payment_method,
            customer_id: customer_id,
            save_card: true,
            save_on_profile: true,
          });
          if (methodRes.status !== StatusCode.OK) {
            uiActions.showError(String(methodRes?.data?.message));
            return null;
          } else {
            const methodId = Number(methodRes?.data?.id ?? methodRes?.data?.data?.id);
            if (!methodId) {
              uiActions.showError(t("Could not save credit card"));
              return null;
            }
            setBookingState(prevState => ({ ...prevState, selectedCardId: methodId }));

            return methodId;
          }
        }
      }
    }
  }

  async function saveBooking() {
    let customerPaymentMethodId = bookingState.selectedCardId;

    //If the new card fields are filled out, set up new payment method and use that payment method
    if (elementsComplete) {
      const newCardRes = await collectCard(bookingState?.bookingParticipants[0]?.id);
      if (newCardRes?.customer_payment_method_id) {
        customerPaymentMethodId = newCardRes?.customer_payment_method_id;
      }
    }

    dispatch(enqueue());

    const holder = bookingState.bookingParticipants[0];
    const primaryLocationId = bookingState.selectedLocationIds[0];
    const primarySegmentIds = bookingState.locationDurations?.[primaryLocationId]?.[bookingState.durationKey];
    if (!primaryLocationId || !primarySegmentIds?.length) {
      dispatch(dequeue());
      uiActions.showError(t("Select a bay"));
      return;
    }
    const participants = Array.from({ length: bookingState.guestsAmount }, (_, index) => {
      const person = bookingState.bookingParticipants[index];
      return {
        customer_id: person?.id ?? holder?.id,
        guest: index > 0 && !person?.id,
      };
    });

    //Main reservation
    const bookingRes = await PostReservationBooking(
      {
        customer_id: holder?.id,
        module_id: module?.id,
        location_id: primaryLocationId,
        segment_ids: primarySegmentIds,
        date: formatDate(bookingDate),
        quantity: bookingState?.guestsAmount,
        customer_payment_method_id: customerPaymentMethodId,
        reservation_participants: participants,
      },
      false,
    );

    if (bookingRes?.status !== StatusCode.OK) {
      uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes?.data : t("Error making booking"));
      dispatch(dequeue());
      return;
    }

    //Additional reservations for other locations that were selected
    for (const locationId in bookingState.locationDurations) {
      if (
        bookingState.selectedLocationIds.includes(Number(locationId)) &&
        Number(locationId) !== primaryLocationId
      ) {
        const extraBookingRes = await PostReservationBooking(
          {
            customer_id: holder?.id,
            module_id: module?.id,
            location_id: Number(locationId),
            segment_ids: bookingState?.locationDurations[locationId][bookingState?.durationKey],
            date: formatDate(bookingDate),
            quantity: bookingState?.guestsAmount,
            customer_payment_method_id: customerPaymentMethodId,
            reservation_participants: participants,
          },
          false,
        );
        if (extraBookingRes?.status !== StatusCode.OK) {
          uiActions.showError(typeof bookingRes?.data === "string" ? bookingRes?.data : t("Error making booking"));
        }
      }
    }
    dispatch(dequeue());

    const bookingId = bookingRes.status === StatusCode.OK ? bookingRes?.data?.id : undefined;
    if (bookingId && bookingState.note !== "") {
      const bookingNoteRes = await PostReservationBookingNote(
        { reservation_booking_id: bookingId, content: bookingState.note },
        true,
      );
      if (bookingNoteRes.status !== StatusCode.OK) {
        uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_new.035"));
      }
    }

    void closeBookingModal();
    void onSave();
    uiActions.showSuccess(t("Booking created"));
  }

  function closeBookingModal() {
    setBookingState(prevState => ({
      ...prevState,
      guestsAmount: 1,
      selectedTab: 0,
      durationKey: "",
      bookingParticipants: [],
      locationDurations: null,
      selectedLocationIds: [],
      ...createEmptyGuestSearchState<ICustomer>(maxBookingSize),
      customerCreditCards: [],
      selectedCardId: undefined,
      saveCard: false,
      addCard: false,
      note: "",
      bookingStep: BookingStep.Step1,
    }));
    setScheduleSegments([]);
    setStartTime("");
    setBookingDate(date);
    setNoteOpen(false);
    void onCancel();
  }

  function closeNewCustomer() {
    setNewCustomerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: false,
    }));
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    if (
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "")
    ) {
      uiActions.showError(t("secure.facility.reservation.reservation_booking_modal_new.008"));
      return;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber ?? null,
        email: customerInfo.emailAddress ?? null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(customerErrorMessage(t, String(customerRes?.data))));
      return;
    }

    uiActions.showSuccess(t("secure.facility.reservation.reservation_booking_modal_new.010"));
    assignGuest(newCustomerSlotRef.current, customerRes?.data as ICustomer);
    void closeNewCustomer();
  }

  const elementsComplete =
    bookingState.elementComplete.cardNumber === true &&
    bookingState.elementComplete.cardExpiry === true &&
    bookingState.elementComplete.cardCvc === true;

  const creditCardOverride = authStore?.user?.permissions?.reservations_credit_card_override;
  const creditCardNotSelected = !bookingState.selectedCardId && !elementsComplete;

  function openNewCustomerSheet(slotIndex = 0) {
    newCustomerSlotRef.current = slotIndex;
    setNewCustomerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: true,
    }));
  }

  function changeStartTime(nextStart: string) {
    if (!selectedLocation || !locations?.length) {
      return;
    }
    const locationDurations = buildLocationDurations(scheduleSegments, nextStart, selectedLocation, locations);
    const keys = Object.keys(locationDurations[selectedLocation.id] ?? {});
    if (!keys.length) {
      uiActions.showError(t("secure.facility.reservation.reservation.008"));
      return;
    }
    setStartTime(nextStart);
    setBookingState(prevState => {
      const durationKey = keys.includes(prevState.durationKey) ? prevState.durationKey : keys[0];
      const selectedLocationIds = prevState.selectedLocationIds.filter(
        locationId => locationDurations[locationId]?.[durationKey]?.length > 0,
      );
      return { ...prevState, locationDurations, durationKey, selectedLocationIds };
    });
  }

  function renderCustomerOptions(slotIndex: number) {
    return (
      <>
        <div className="ui-select-dropdown-list-item" onClick={() => openNewCustomerSheet(slotIndex)}>
          <p>{t("New Customer")}</p>
        </div>
        {(bookingState.guestSearchResults[slotIndex] ?? []).map((customer: ICustomer, customerIndex: number) => {
          return (
            <Option key={customerIndex} value={customer?.id} extraValues={customer}>
              <div className="flex justify-between">
                <div>
                  <div className="font-semibold text-lg">{customer?.full_name}</div>
                  <div className="text-sm text-gray-500">{customer?.customer_type}</div>
                  <div className="text-sm text-gray-500">{customer?.email}</div>
                  <div className="text-sm text-gray-500">{customer?.phone ? customer?.phone : null}</div>
                </div>
                <div className="flex flex-column justify-between">
                  {customer.has_high_priority_notes ? (
                    <div
                      className="font-medium text-error self-end gap-2 flex"
                      style={{ alignItems: "center" }}
                      onClick={event => {
                        event.stopPropagation();
                        window.open(`/admin/customers/${String(customer.id)}/notes`);
                      }}
                    >
                      <FontAwesomeIcon icon={["far", "circle-exclamation"]} size="lg" />
                      <p>{t("Click to view notes")}</p>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                </div>
              </div>
            </Option>
          );
        })}
      </>
    );
  }

  const durationKeys =
    selectedLocation && bookingState.locationDurations
      ? Object.keys(bookingState.locationDurations[selectedLocation.id] ?? {})
      : [];
  const activeSegment =
    scheduleSegments.find(segment => segment.location_id === selectedLocation?.id && segment.start_time === startTime) ??
    selectedSegment;
  const activeEventId = eventIdentity(activeSegment);
  const startOptions = selectedLocation
    ? Array.from(
        new Set(
          scheduleSegments
            .filter(segment => segment.location_id === selectedLocation.id && isOpenForBooking(segment, activeEventId))
            .map(segment => segment.start_time),
        ),
      )
        .sort()
        .filter(
          time =>
            Object.keys(
              buildLocationDurations(scheduleSegments, time, selectedLocation, locations ?? [])[selectedLocation.id] ??
                {},
            ).length > 0,
        )
    : [];
  const bayOptions = (locations ?? []).filter(
    location =>
      bookingState.locationDurations?.[location.id]?.[bookingState.durationKey]?.length > 0 &&
      !bookingState.selectedLocationIds.includes(location.id),
  );
  const selectedBays = bookingState.selectedLocationIds
    .map(locationId => (locations ?? []).find(location => location.id === locationId))
    .filter((location): location is ILocation => Boolean(location));
  const guestCountLabel =
    bookingState.guestsAmount === 1 ? t("1 guest") : t("{{count}} guests", { count: bookingState.guestsAmount });
  const eventLabel = activeSegment?.league_id
    ? t("secure.facility.reservation.reservation.013")
    : activeSegment?.tournament_id
      ? t("secure.facility.reservation.reservation.014")
      : null;
  const drawerSubtitle = [
    selectedBays.map(location => location.title).join(", ") || null,
    eventLabel,
    startTime ? moment(startTime, "HH:mm:ss").format("h:mm A") : null,
    moment(bookingDate).format("MMM DD, YYYY"),
    guestCountLabel,
  ]
    .filter(Boolean)
    .join(" • ");
  const noteCount = bookingState.note.trim() ? 1 : 0;
  const cardRequired =
    !creditCardOverride && Boolean(activeSegment?.credit_card_required) && creditCardNotSelected;

  return (
    <>
      <Drawer
        className="reservation-new-booking-drawer"
        open={open}
        title={t("New booking")}
        subtitle={drawerSubtitle}
        cancelText={t("Cancel")}
        okText={t("Create booking")}
        showCancel
        onClose={closeBookingModal}
        onCancel={closeBookingModal}
        onOk={() => void saveBooking()}
        okDisabled={
          !bookingState.bookingParticipants[0]?.id ||
          !bookingState.guestsAmount ||
          !bookingState.locationDurations ||
          !bookingState.durationKey ||
          !bookingState.selectedLocationIds.length ||
          scheduleLoading ||
          cardRequired
        }
      >
        <div className="reservation-new-booking">
          {scheduleLoading ? (
            <div className="reservation-new-booking__loading">
              <Spin />
              <p>{t("Loading booking...")}</p>
            </div>
          ) : (
          <>
          <section className="reservation-new-booking__section">
            <div className="reservation-new-booking__section-header">
              <h2>{t("Guest(s)")}</h2>
              <div className="reservation-add-guests__stepper">
                <button
                  type="button"
                  aria-label={t("Remove guest")}
                  disabled={bookingState.guestsAmount <= 1}
                  onClick={() => changeGuestCount(bookingState.guestsAmount - 1)}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <span>{bookingState.guestsAmount}</span>
                <button
                  type="button"
                  aria-label={t("Add guest")}
                  disabled={bookingState.guestsAmount >= maxBookingSize}
                  onClick={() => changeGuestCount(bookingState.guestsAmount + 1)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>
            <div className="reservation-new-booking__guests">
              {Array.from({ length: bookingState.guestsAmount }, (_, index) => {
                const participant = bookingState.bookingParticipants[index];
                const label = index === 0 ? t("Booking holder") : t("Guest {{number}}", { number: index + 1 });
                return (
                  <React.Fragment key={index}>
                    <div className="reservation-add-guests__field">
                      <span>{label}</span>
                      {participant ? (
                        <div className="reservation-add-guests__selected">
                          <span>{participant.full_name}</span>
                          <button
                            type="button"
                            aria-label={t("Remove guest")}
                            onClick={() => assignGuest(index, null)}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>
                      ) : (
                        <Select
                          showSearch
                          onSearch={(query: string) => handleCustomerSearch(query, index)}
                          onChange={(_id: number, customer: ICustomer) => assignGuest(index, customer)}
                          searchValue={bookingState.guestQueries[index] ?? ""}
                          showDropDownOnFocus
                          searching={bookingState.searching}
                          placeholder={t("Search a customer")}
                          usePortal
                          leftIcon={
                            <FontAwesomeIcon className="reservation-add-guests__search-icon" icon={faMagnifyingGlass} />
                          }
                        >
                          {renderCustomerOptions(index)}
                        </Select>
                      )}
                    </div>
                    {index === 0 ? (
                      <p className="reservation-new-booking__hint">
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <span>{t("Guests left blank use the booking holder until you assign them.")}</span>
                      </p>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          </section>

          <hr className="reservation-new-booking__divider" />

          <section className="reservation-new-booking__section">
            <div className="reservation-new-booking__section-header">
              <h2>{t("Date, time & location")}</h2>
            </div>
            <DatePickerInput
              label={t("Date")}
              months={1}
              position="left"
              startingDate={bookingDate}
              setStartingDate={(nextDate: Date) => void loadSchedule(nextDate, startTime)}
              usePortal
            />
            <div className="reservation-new-booking__time-row">
              {startOptions.length ? (
                <Select
                  key={startTime || "start"}
                  block
                  label={t("Start time")}
                  defaultValue={startTime}
                  usePortal
                  leftIcon={<FontAwesomeIcon className="reservation-new-booking__field-icon" icon={faClock} />}
                  onChange={(value: string) => changeStartTime(value)}
                >
                  {startOptions.map(time => (
                    <Option key={time} value={time} name={moment(time, "HH:mm:ss").format("hh:mm A")}>
                      {moment(time, "HH:mm:ss").format("hh:mm A")}
                    </Option>
                  ))}
                </Select>
              ) : (
                <div className="booking-modal-spinner">
                  <Spin />
                </div>
              )}
              {durationKeys.length ? (
                <Select
                  key={`${startTime}-${bookingState.durationKey}`}
                  block
                  label={t("Duration")}
                  defaultValue={bookingState.durationKey}
                  usePortal
                  onChange={(value: string) => handleDropDownChange(value, "durationKey")}
                >
                  {durationKeys.map(durationKey => (
                    <Option key={durationKey} value={durationKey} name={formatDurationLabel(durationKey)}>
                      {formatDurationLabel(durationKey)}
                    </Option>
                  ))}
                </Select>
              ) : (
                <div className="booking-modal-spinner">
                  <Spin />
                </div>
              )}
            </div>
            <div className="reservation-new-booking__bay">
              <div
                className={classNames("reservation-new-booking__bay-field", {
                  "reservation-new-booking__bay-field--warning": selectedBays.length === 0,
                })}
              >
                <FontAwesomeIcon className="reservation-new-booking__field-icon" icon={faLocationDot} />
                {selectedBays.map(location => (
                  <span className="reservation-new-booking__bay-tag" key={location.id}>
                    {location.title}
                    <button type="button" aria-label={t("Remove bay")} onClick={() => removeBay(location.id)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </span>
                ))}
                <Select
                  key={bookingState.selectedLocationIds.join("-") || "bays"}
                  placeholder={t("Select bays...")}
                  usePortal
                  onChange={(locationId: number) => addBay(locationId)}
                >
                  {bayOptions.map(location => (
                    <Option key={location.id} value={location.id} name={location.title}>
                      {location.title}
                    </Option>
                  ))}
                </Select>
              </div>
              {selectedBays.length === 0 ? (
                <p className="reservation-new-booking__warning">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  <span>{t("A bay must be selected.")}</span>
                </p>
              ) : selectedLocation && bookingState.selectedLocationIds.includes(selectedLocation.id) ? (
                <p className="reservation-new-booking__support">
                  {t("{{bay}} is from the slot you picked.", { bay: selectedLocation.title })}
                </p>
              ) : null}
            </div>
          </section>

          <hr className="reservation-new-booking__divider" />

          <section className="reservation-new-booking__section">
            <div className="reservation-new-booking__section-header">
              <h2>{t("Credit card")}</h2>
              <p>{t("Choose a saved card on file or add one manually.")}</p>
            </div>
            {bookingState.customerCreditCards?.length ? (
              <div className="ui-checkbox-group">
                {bookingState.customerCreditCards.map((paymentMethod: Record<string, any>, index: number) => (
                  <div
                    key={index}
                    onClick={() => onSelectCreditCard(Number(paymentMethod.id))}
                    className={classNames("ui-checkbox-group-item", {
                      "ui-checkbox-group-item_selected": Number(paymentMethod.id) === Number(bookingState.selectedCardId),
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <div className="ui-checkbox-group-item-lead">
                        <FontAwesomeIcon size="1x" icon={["far", "credit-card"]} />
                      </div>
                      <div className="ui-checkbox-group-item-text">
                        <p className="text-sm text-medium text-gray-700">
                          {valueToString(String(paymentMethod.brand), "capitalize")}{" "}
                          {t("secure.facility.reservation.reservation_booking_modal_new.036")} {paymentMethod.last4}
                        </p>
                        <p className="text-sm text-regular text-gray-600">
                          {t("secure.facility.reservation.reservation_booking_modal_new.037")} {paymentMethod.expiry}
                        </p>
                      </div>
                    </div>
                    <div style={{ pointerEvents: "none" }}>
                      <Checkbox
                        size="medium"
                        value={paymentMethod.id}
                        checked={Number(paymentMethod.id) === Number(bookingState.selectedCardId)}
                        onChange={() => undefined}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="reservation-booking-details__empty">
                <FontAwesomeIcon icon={faCircleInfo} />
                <div>
                  <div className="reservation-booking-details__empty-title">{t("Add a credit card")}</div>
                  <div className="reservation-booking-details__empty-support">
                    {t("Customer does not have any saved credit card on their account.")}
                  </div>
                </div>
              </div>
            )}
            {bookingState.addCard ? (
              <div className="reservation-new-booking__card-form">
                {open ? <CardSection onChange={handleCardSectionChange} type="two-row" /> : null}
                <Checkbox
                  label={t("secure.facility.reservation.reservation_booking_modal_new.023")}
                  size="small"
                  value={bookingState.saveCard}
                  checked={bookingState.saveCard}
                  onChange={() => setBookingState(prevState => ({ ...prevState, saveCard: !prevState.saveCard }))}
                />
              </div>
            ) : null}
            <button
              type="button"
              className="reservation-booking-details__text-button reservation-booking-details__text-button--small"
              onClick={() => setBookingState(prevState => ({ ...prevState, addCard: !prevState.addCard }))}
            >
              <FontAwesomeIcon icon={bookingState.addCard ? faXmark : faPlus} />
              {bookingState.addCard
                ? t("Cancel")
                : t("Add new card")}
            </button>
          </section>

          <hr className="reservation-new-booking__divider" />

          <section className="reservation-new-booking__section">
            <div className="reservation-new-booking__section-header">
              <h2>{t("Notes ({{count}})", { count: noteCount })}</h2>
            </div>
            {noteOpen || bookingState.note ? (
              <div className="reservation-new-booking__note">
                <TextField id="note" rows={3} value={bookingState.note} onChange={handleInputChange} />
              </div>
            ) : (
              <div className="reservation-booking-details__empty">
                <FontAwesomeIcon icon={faCircleInfo} />
                <div>
                  <div className="reservation-booking-details__empty-title">{t("Add a booking note")}</div>
                  <div className="reservation-booking-details__empty-support">
                    {t("There are no saved notes for this booking.")}
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              className="reservation-booking-details__text-button reservation-booking-details__text-button--small"
              onClick={() => {
                if (noteOpen || bookingState.note) {
                  setBookingState(prevState => ({ ...prevState, note: "" }));
                  setNoteOpen(false);
                  return;
                }
                setNoteOpen(true);
              }}
            >
              <FontAwesomeIcon icon={noteOpen || bookingState.note ? faXmark : faPlus} />
              {noteOpen || bookingState.note ? t("Cancel") : t("Add note")}
            </button>
          </section>
          </>
          )}
        </div>
      </Drawer>

      <NewCustomer
        newCustomerSheetActive={newCustomerState.newCustomerSheetActive}
        onCancel={closeNewCustomer}
        onOk={info => void createNewCustomer(info)}
        searchValue={newCustomerState.currentSearch}
      />
    </>
  );
}
